import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  forwardRef,
} from 'react';
import { NimblePicker, EmojiData, CategoryName } from 'emoji-mart';
import { CcfEmojiIcon, useTranslator, CcfTooltip } from '@nice-ccf/ui-controls';
import { Popper, ClickAwayListener, PopperPlacementType, Box } from '@mui/material';
import emojiData from './emoji-data';
import CcfEmojiPickerStyles from './ccf-emoji-picker-style';

/**
 * type for emoji set
 */
export type EmojiSet = 'apple' | 'google' | 'twitter' | 'facebook';

/**
 * Enum for SkinTone
 */
export enum SkinTone {
  DEFAULT = 1,
  LIGHT = 2,
  MEDIUM_LIGHT = 3,
  MEDIUM = 4,
  MEDIUM_DARK = 5,
  DARK = 6,
}

/**
 * interface for CcfEmojiProps
 */
export interface CcfEmojiProps {
  /**
   * @remarks  Used to show/hide emoji tooltip
   */
  showTooltip?: boolean;
  /**
   * @remarks  Used to set the size of the emojis
   */
  size?: number;
  /**
   * @remarks  Enable/disable auto focus for search input box
   */
  searchAutoFocus?: boolean;
  /**
   * @remarks Used to set default emoji skin color
   */
  defaultSkin?: SkinTone;
  /**
   * @remarks  Used select emoji set eg. -'apple' | 'google' | 'twitter' | 'facebook';
   */
  set?: EmojiSet;
  /**
   * @remarks  Used to set perLine emoji count
   */
  perLine?: number;
  /**
   * @remarks  Used to set placement of emoji picker
   */
  placement?: PopperPlacementType;
  /**
   * @remarks  callback method used to return selected emoji to parent component
   */
  onSelect: (data: EmojiData) => void;
  /**
   * @remarks  to exclude category from emoji. Ex: flags
   */
  exclude?: CategoryName[];
}
const defaultOptions: CcfEmojiProps = {
  showTooltip: true,
  size: 23,
  searchAutoFocus: true,
  defaultSkin: SkinTone.DEFAULT,
  set: 'apple',
  perLine: 6,
  exclude: ['flags'],
  placement: 'top-start',
  onSelect: () => {
    return;
  },
};
/**
 * Used to show Emoji picker for the application
 * @param props -?- CcfEmojiProps
 * @example - `<CcfEmojiPicker {...props}/>`
 */
export const CcfEmojiPicker = forwardRef<HTMLButtonElement, CcfEmojiProps>(
  (props, ref) => {
    const [options, setEmojiOptions] = useState(defaultOptions);
    const [openPicker, setEmojiPicker] = useState(false);
    const [translate] = useTranslator();
    const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);
    const wrapperRef = useRef(null);

    const i18n = {
      search: translate('search'),
      notfound: translate('noEmojiFound'),
      categories: {
        search: translate('searchResults'),
        recent: translate('frequentlyUsed'),
        smileys: translate('smileysAndEmotions'),
        people: translate('peopleAndBody'),
        nature: translate('animalAndNature'),
        foods: translate('foodAndDrink'),
        activity: translate('relatedInteractions'),
        places: translate('travelAndPlaces'),
        objects: translate('objects'),
        symbols: translate('symbols'),
      },
      skintones: {
        1: translate('defaultSkinTone'),
        2: translate('lightSkinTone'),
        3: translate('mediumLightSkinTone'),
        4: translate('mediumSkinTone'),
        5: translate('mediumDarkSkinTone'),
        6: translate('darkSkinTone'),
      },
    };
    const emojiStyles = CcfEmojiPickerStyles();

    /**
     * Method to handle Emoji selection
     * @param emojiData - emoji data
     * @example `handleEmojiClick(data)`
     */
    const handleEmojiClick = (emojiData: EmojiData) => {
      options.onSelect(emojiData);
      setAnchorEl(null);
      setEmojiPicker(false);
    };

    /**
     * Used to update emoji options
     * @param props -?- CcfEmojiProps
     * @example - `updateEmojiOptions(props)`
     */
    const updateEmojiOptions = (props: CcfEmojiProps) => {
      setEmojiOptions((prevState: CcfEmojiProps) => ({
        ...prevState,
        ...props,
      }));
    };

    useEffect(() => {
      updateEmojiOptions(props);
    }, [props]);

    /**
     * This method t toggle emojiPicker
     *  @param event - Event
     *  @example - toggleEmojiPicker()
     */
    const toggleEmojiPicker = useCallback((event: any) => {
      setAnchorEl(anchorEl ? null : event.currentTarget);
      setEmojiPicker((previousOpen) => !previousOpen);
    }, []);

    /**
     * Method to handle emoji picker if click outside
     * @param event - Event
     * @example `onOutsideClick()`
     */
    const onOutsideClick = useCallback((event: Event) => {
      const ref = wrapperRef as React.RefObject<HTMLElement>;
      if (ref.current && ref.current.contains(event.target as Node)) {
        return;
      }
      setAnchorEl(null);
      setEmojiPicker(false);
    }, []);

    return (
      <div ref={wrapperRef} style={{ margin: '5px' }}>
        <CcfTooltip title={translate('addEmoji')} disableHoverListener={openPicker}>
          <span role="button" tabIndex = {-1} 
            onKeyUp = {(e) => {if(e.key === 'Enter') toggleEmojiPicker(e)}} onClick={toggleEmojiPicker} style={{ cursor: 'pointer' }} ref={ref}>
            <CcfEmojiIcon />
          </span>
        </CcfTooltip>
        <Popper
          open={openPicker}
          anchorEl={anchorEl}
          placement={options.placement}
          style={{ zIndex: 2000 }}>
          <ClickAwayListener onClickAway={onOutsideClick}>
            <Box sx={emojiStyles} data-testid = 'emoji-box'>
              <NimblePicker
                data={emojiData}
                onSelect={(data) => handleEmojiClick(data)}
                emojiTooltip={options.showTooltip}
                title={''}
                emoji={''}
                i18n={i18n}
                emojiSize={options.size}
                autoFocus={options.searchAutoFocus}
                color={'#009BD7'}
                style={{ width: '228px' }}
                defaultSkin={options.defaultSkin}
                showPreview={false}
                perLine={options.perLine}
                set={options.set}
                enableFrequentEmojiSort={true}
                exclude={options.exclude}
              />
            </Box>
          </ClickAwayListener>
        </Popper>
      </div>
    );
  })

export default CcfEmojiPicker;
